import { logMessage, parseUrlForGroupId, formatCurrency, formatDate } from './utils.mjs';
import { renderModal, closeModal } from './ui.mjs';
import { createFAB, Icons } from './components.mjs';
import { USER_PLUS_ICON } from './receiptHelpers.mjs';
import {
    selectedItems,
    currentExpense,
    currentParticipants,
    selectionHistoryStatePushed,
    setCurrentView
} from './receiptState.mjs';
import { editItem } from './receiptItemEditor.mjs';
import { editCharge, addItem, editExpenseDetails, openPayerSelector } from './receiptItemEditor.mjs';
import { unassignParticipant, openAssignmentModal } from './receiptAssignment.mjs';
import { openReceiptDrawer } from './receiptDrawer.mjs';
import { addDeleteButtonToModal } from './uiHelpers.mjs';
import { deleteExpense } from './api.mjs';
import { cache } from './main.mjs';
import router from './router.mjs';
import { modalRegistry } from './modalRegistry.mjs';
import t from 'virtual:i18n';

// Render receipt view for an itemized expense as a modal
export function renderReceiptView(expense, participants, resetSelections = true) {
    if (!expense || expense.expenseType !== 'ITEMIZED' || !expense.itemizedData) {
        logMessage('Cannot render receipt: Not an itemized expense or missing data', 'error');
        return;
    }

    setCurrentView(expense, participants);

    // Reset selected items only when explicitly requested
    if (resetSelections) {
        Object.keys(selectedItems).forEach(key => delete selectedItems[key]);
        const { selectionHistoryStatePushed: statePushed } = require('./receiptState.mjs');
        if (statePushed) {
            // Reset the flag
            require('./receiptState.mjs').selectionHistoryStatePushed = false;
        }
    }

    // --- content construction ---

    // Check if all items are assigned
    let allAssigned = true;
    if (expense.itemizedData && expense.itemizedData.items) {
        for (const item of expense.itemizedData.items) {
            let assignedQty = 0;
            if (item.assignees) {
                item.assignees.forEach(a => assignedQty += a.quantity);
            }
            if (assignedQty < item.quantity) {
                allAssigned = false;
                break;
            }
        }
    }

    const instructionIcon = allAssigned ? '🎉' : '👆';
    const instructionText = allAssigned ? t.allItemsAssigned : t.tapToAssign;

    // Main Container for Modal Body
    const content = document.createElement('div');
    content.className = 'receipt-modal-content';
    Object.assign(content.style, {
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        paddingBottom: '80px' // Space for FAB
    });

    // Header Instruction
    const headerDiv = document.createElement('div');
    headerDiv.className = 'receipt-header';
    headerDiv.innerHTML = `
        <div class="receipt-instruction" style="justify-content:center;">
            <span class="tap-icon">${instructionIcon}</span> ${instructionText}
        </div>
    `;
    content.appendChild(headerDiv);

    // Items List
    const termsList = document.createElement('div');
    termsList.className = 'receipt-items-list';

    const hintSeen = localStorage.getItem('receipt_hint_seen') === 'true';


    if (expense.itemizedData.items) {
        expense.itemizedData.items.forEach((item, index) => {
            const itemRow = document.createElement('div');
            itemRow.className = 'receipt-item';
            itemRow.dataset.itemIndex = index;

            let totalAssigned = 0;
            let badgesHtml = '';
            if (item.assignees) {
                badgesHtml = '<div class="assignee-badges">';
                item.assignees.forEach(a => {
                    if (a.participants?.length > 0) {
                        const names = a.participants.map(p => participants[p]).join(', ');
                        const displayName = item.quantity > 1 ? `(${a.quantity}) ${names}` : names;
                        badgesHtml += `<span class="assignee-badge">${displayName}</span>`;
                        totalAssigned += a.quantity;
                    }
                });
                badgesHtml += '</div>';
            }

            const available = item.quantity - totalAssigned;
            if (selectedItems[index] === undefined) selectedItems[index] = 0;

            const leftCol = document.createElement('div');
            leftCol.className = 'receipt-item-left';

            if (available === 0) {
                leftCol.innerHTML = '<span class="status-check">✓</span>';
                itemRow.classList.add('fully-assigned');
            } else {
                let displayQty;
                if (selectedItems[index] > 0) {
                    itemRow.classList.add('selected');
                    displayQty = `<span class="selected-count">${selectedItems[index]}</span><span class="total-qty">/${available}</span>`;
                } else {
                    displayQty = available;
                }
                leftCol.innerHTML = `<span class="status-qty">${displayQty}</span>`;

                itemRow.classList.add('clickable');
                itemRow.addEventListener('click', () => {
                    // Mark hint as seen
                    if (!hintSeen) {
                        localStorage.setItem('receipt_hint_seen', 'true');
                    }

                    const { selectedItems: stateItems, selectionHistoryStatePushed: statePushed } = require('./receiptState.mjs');
                    const wasEmpty = Object.values(stateItems).reduce((a, b) => a + b, 0) === 0;
                    const toSelect = Math.min(1, available); // Default select 1

                    // Toggle logic for single quantity items or increment for multi
                    if (available === 1) {
                        stateItems[index] = stateItems[index] === 0 ? 1 : 0;
                    } else {
                        stateItems[index]++;
                        if (stateItems[index] > available) stateItems[index] = 0;
                    }

                    const isEmpty = Object.values(stateItems).reduce((a, b) => a + b, 0) === 0;

                    if (wasEmpty && !isEmpty && !statePushed) {
                        history.pushState({ selectionMode: true }, '');
                        require('./receiptState.mjs').selectionHistoryStatePushed = true;
                    } else if (!wasEmpty && isEmpty && statePushed) {
                        history.back();
                        return;
                    }
                    renderReceiptView(expense, participants, false);
                });

                if (!hintSeen && index === 0 && available > 0) {
                    const hint = document.createElement('div');
                    hint.className = 'strobe-hint';
                    hint.innerHTML = '👆';
                    itemRow.appendChild(hint);
                }
            }


            const middleCol = document.createElement('div');
            middleCol.className = 'receipt-item-middle';
            const nameDiv = document.createElement('div');
            nameDiv.className = 'item-name';
            nameDiv.textContent = item.name;
            middleCol.appendChild(nameDiv);
            if (badgesHtml) {
                const badgeContainer = document.createElement('div');
                badgeContainer.innerHTML = badgesHtml;
                // Add click handlers for unassign logic if needed (reuse existing pattern)
                Array.from(badgeContainer.querySelectorAll('.assignee-badge')).forEach((b, i) => {
                    b.addEventListener('click', (e) => {
                        e.stopPropagation();
                        unassignParticipant(expense, index, item.assignees[i], participants);
                    });
                });
                middleCol.appendChild(badgeContainer);
            }

            const rightCol = document.createElement('div');
            rightCol.className = 'receipt-item-right';
            const priceDiv = document.createElement('div');
            priceDiv.className = 'item-price';
            priceDiv.textContent = formatCurrency(item.price, expense.currency);
            rightCol.appendChild(priceDiv);

            const moreBtn = document.createElement('div');
            moreBtn.className = 'item-menu-dots';
            moreBtn.textContent = '⋮';
            moreBtn.onclick = (e) => {
                e.stopPropagation();
                if (!hintSeen) localStorage.setItem('receipt_hint_seen', 'true');
                editItem(item, index, expense, participants);
            };

            rightCol.appendChild(moreBtn);

            itemRow.appendChild(leftCol);
            itemRow.appendChild(middleCol);
            itemRow.appendChild(rightCol);
            termsList.appendChild(itemRow);
        });
    }

    // Charges (similar logic)
    if (expense.itemizedData.additionalCharges) {
        expense.itemizedData.additionalCharges.forEach((charge, index) => {
            const row = document.createElement('div');
            row.className = 'receipt-item additional-charge';

            // Spacer
            row.appendChild(document.createElement('div'));

            const mid = document.createElement('div');
            mid.className = 'receipt-item-middle';
            const name = document.createElement('div');
            name.className = 'item-name';
            name.style.fontStyle = 'italic';
            name.textContent = charge.name;
            mid.appendChild(name);
            row.appendChild(mid);

            const right = document.createElement('div');
            right.className = 'receipt-item-right';
            const price = document.createElement('div');
            price.className = 'item-price';
            const val = charge.isDiscount ? -Math.abs(charge.value) : charge.value;
            price.textContent = formatCurrency(val, expense.currency);
            right.appendChild(price);

            const more = document.createElement('div');
            more.className = 'item-menu-dots';
            more.textContent = '⋮';
            more.onclick = (e) => {
                e.stopPropagation();
                editCharge(charge, index, expense, participants);
            };
            right.appendChild(more);

            row.appendChild(right);
            termsList.appendChild(row);
        });
    }
    content.appendChild(termsList);

    // Footer
    const footerDiv = document.createElement('div');
    footerDiv.className = 'receipt-footer';

    const addItemBtn = document.createElement('div');
    addItemBtn.className = 'add-item-row';
    addItemBtn.innerHTML = `<span>+</span> ${t.addItem}`;
    addItemBtn.onclick = () => {
        if (!hintSeen) localStorage.setItem('receipt_hint_seen', 'true');
        addItem(expense, participants);
    };

    footerDiv.appendChild(addItemBtn);

    const totalRow = document.createElement('div');
    totalRow.className = 'total-row';
    totalRow.innerHTML = `
        <div class="total-label" style="font-weight:700; color:#555;">${t.total} (<span class="currency-link" style="color:var(--color-primary); font-weight:600; cursor:pointer; text-decoration:underline;">${expense.currency || 'USD'}</span>)</div>
        <div class="total-amount" style="font-weight:700; color:#333;">${formatCurrency(expense.amount, expense.currency)}</div>
    `;
    totalRow.querySelector('.currency-link').onclick = async (e) => {
        e.stopPropagation();
        const { openCurrencyPicker } = await import('./currencyPicker.mjs');
        openCurrencyPicker(expense.currency || 'USD', async (newCurrency) => {
            if (newCurrency === expense.currency) return;
            const groupId = parseUrlForGroupId();
            if (!groupId) return;
            try {
                const { editExpense } = await import('./api.mjs');
                const { cloneItemizedData } = await import('./receiptHelpers.mjs');
                const itemizedDataInput = cloneItemizedData(expense.itemizedData);
                const updatedGroup = await editExpense(groupId, expense.id, {
                    itemizedData: itemizedDataInput,
                    expenseType: expense.expenseType,
                    currency: newCurrency,
                    date: expense.date,
                    description: expense.description
                });
                const updatedExpense = updatedGroup.expenses.find(e => e.id === expense.id);
                if (updatedExpense) {
                    renderReceiptView(updatedExpense, participants, false);
                }
            } catch (err) {
                console.error(err);
                alert('Failed to update currency.');
            }
        });
    };
    footerDiv.appendChild(totalRow);

    const hasPayer = expense.payers && expense.payers.length > 0;
    if (hasPayer) {
        const paidRow = document.createElement('div');
        paidRow.className = 'paid-by-row';
        let payerText = '';
        if (expense.payers.length === 1) {
            payerText = participants[expense.payers[0]] || 'Unknown';
        } else {
            const summaries = expense.payers.map((pIdx, i) => {
                const amt = expense.payerAmounts ? expense.payerAmounts[i] : 0;
                return `${participants[pIdx] || 'Unknown'} (${formatCurrency(amt, expense.currency)})`;
            });
            payerText = summaries.join(', ');
        }
        paidRow.innerHTML = `<span style="font-size:14px; font-weight:500; color:#555;">${t.paidByText}</span> <span class="payer-link" style="color:var(--color-primary); font-weight:700; font-size:16px; cursor:pointer; text-decoration:underline;">${payerText}</span>`;
        paidRow.querySelector('.payer-link').onclick = (e) => {
            e.stopPropagation();
            openPayerSelector(expense, participants);
        };
        footerDiv.appendChild(paidRow);
    } else {
        const paidRow = document.createElement('div');
        paidRow.className = 'paid-by-row';
        paidRow.innerHTML = `<span class="payer-link" style="color:var(--color-primary); font-weight:700; font-size:16px; cursor:pointer; text-decoration:underline; display:inline-block; padding-top:4px;">${t.whoPaidForIt}</span>`;
        paidRow.querySelector('.payer-link').onclick = (e) => {
            e.stopPropagation();
            openPayerSelector(expense, participants);
        };
        footerDiv.appendChild(paidRow);
    }

    // --- Inline Receipt Image (below payer) ---
    const userEmail = localStorage.getItem('splitpal_user_email');
    const isOwner = userEmail && userEmail === expense.itemizedData?.owner;
    const isReceiptAvailable = expense.itemizedData?.receiptAvailable === true;

    if (expense.itemizedData && (isReceiptAvailable || isOwner)) {
        const receiptSection = document.createElement('div');
        receiptSection.className = 'receipt-inline-section';
        Object.assign(receiptSection.style, {
            marginTop: '12px',
            borderTop: '1px solid #f0f0f0',
            paddingTop: '12px'
        });

        // Owner-only visibility toggle
        if (isOwner) {
            const visibilityDiv = document.createElement('div');
            visibilityDiv.style.marginBottom = '12px';
            visibilityDiv.innerHTML = `
                <div style="display:flex; justify-content:space-between; align-items:center; gap:16px;">
                    <div>
                        <div style="font-size:14px; font-weight:500; margin-bottom:2px;">${t.imageVisibility}</div>
                        <div style="font-size:12px; color:#888; line-height:1.4;">${t.anyoneWithAccess}</div>
                    </div>
                    <label class="toggle-switch" style="flex-shrink:0;">
                        <input type="checkbox" ${isReceiptAvailable ? 'checked' : ''}>
                        <span class="slider"></span>
                    </label>
                </div>
            `;
            const checkbox = visibilityDiv.querySelector('input');
            checkbox.addEventListener('change', async (e) => {
                e.stopPropagation();
                const { updateReceiptVisibility } = await import('./receiptDrawer.mjs');
                await updateReceiptVisibility(expense, e.target.checked, participants);
            });
            receiptSection.appendChild(visibilityDiv);
        }

        // Image container
        const imgContainer = document.createElement('div');
        imgContainer.className = 'receipt-image-container';
        Object.assign(imgContainer.style, {
            borderRadius: '12px',
            overflow: 'hidden',
            background: '#f5f5f5',
            minHeight: '120px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
        });

        const loadingText = document.createElement('div');
        loadingText.innerText = t.loadingReceipt;
        loadingText.style.cssText = 'color:#aaa; font-size:13px; padding:20px;';
        imgContainer.appendChild(loadingText);
        receiptSection.appendChild(imgContainer);
        footerDiv.appendChild(receiptSection);

        // Fetch and display image
        const groupId = parseUrlForGroupId();
        if (groupId) {
            const cacheKey = `receipt_base64_${groupId}_${expense.id}`;
            const cachedBase64 = localStorage.getItem(cacheKey);

            const showImage = (src) => {
                imgContainer.innerHTML = '';
                const img = document.createElement('img');
                img.src = src;
                img.className = 'receipt-image';
                Object.assign(img.style, {
                    width: '100%',
                    borderRadius: '12px',
                    display: 'block'
                });
                imgContainer.appendChild(img);
            };

            if (cachedBase64) {
                showImage(cachedBase64);
            } else {
                import('./api.mjs').then(({ getExpenseImageUrl }) => {
                    return getExpenseImageUrl(groupId, expense.id);
                }).then(async url => {
                    if (!url) {
                        imgContainer.innerHTML = `<div style="padding:20px; color:#aaa; font-size:13px; text-align:center;">${t.imageNotAvailable}</div>`;
                        return;
                    }
                    try {
                        const response = await fetch(url);
                        if (!response.ok) throw new Error('Network error');
                        const blob = await response.blob();
                        const reader = new FileReader();
                        reader.onloadend = () => {
                            const base64data = reader.result;
                            showImage(base64data);
                            try { localStorage.setItem(cacheKey, base64data); } catch (e) { /* quota exceeded */ }
                        };
                        reader.readAsDataURL(blob);
                    } catch (e) {
                        showImage(url);
                    }
                }).catch(() => {
                    imgContainer.innerHTML = `<div style="padding:20px; color:#aaa; font-size:13px; text-align:center;">${t.failedToLoadImage}</div>`;
                });
            }
        }
    }
    content.appendChild(footerDiv);

    // --- Settlement Cards (If only 1 expense in tab) ---
    if (cache.group && cache.group.expenses && cache.group.expenses.length === 1) {
        import('./settlement.mjs').then(({ renderSettlementCards }) => {
            const settlementContainer = document.createElement('div');
            settlementContainer.className = 'receipt-settlement-section';
            Object.assign(settlementContainer.style, {
                marginTop: '8px',
                paddingTop: '8px'
            });
            
            // Re-route click logic on modal to close modal when navigating
            const originalOnCardClick = (entry) => {
                const modal = document.getElementById('receipt-view-modal');
                if (modal) {
                    import('./ui.mjs').then(({ closeModal }) => {
                        closeModal(modal, true); // Close without triggering history back
                    });
                }
                router.navigate(`/home/${cache.group.id}/payback/${entry.creditor}/${entry.debtor}`);
            };

            renderSettlementCards(settlementContainer, cache.group, { 
                isModal: true,
                onCardClick: originalOnCardClick
            });
            content.appendChild(settlementContainer);
        });
    }

    // --- Share Section ---
    import('./ui.mjs').then(({ renderShareSection }) => {
        const groupId = parseUrlForGroupId();
        if (groupId) {
            renderShareSection(content, groupId, expense.id);
        }
    });




    // Modal Construction
    let modal = document.getElementById('receipt-view-modal');

    // Header Title
    const titleContent = document.createElement('div');
    titleContent.className = 'receipt-header-title-container';
    titleContent.innerHTML = `
        <div class="receipt-header-title-wrapper">
            <span class="receipt-header-title-text">${expense.description}</span>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#888" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" class="receipt-header-edit-icon">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
            </svg>
        </div>
        <div class="receipt-header-subtitle">${formatDate(expense.date)}${!hasPayer ? ' <span class="unassigned-label">(who paid?)</span>' : ''}</div>
    `;
    titleContent.onclick = () => editExpenseDetails(expense, participants);

    // Dynamic FAB / Assign Button
    const totalSelected = Object.values(selectedItems).reduce((a, b) => a + b, 0);

    let fab = modal ? modal.querySelector('.receipt-fab') : null;

    if (totalSelected > 0) {
        if (!fab) {
            fab = createFAB({
                text: t.assignSelected,
                icon: USER_PLUS_ICON,
                onClick: (e) => {
                    e.stopPropagation();
                    openAssignmentModal(expense, participants);
                }
            });
            const sheetContent = modal ? modal.querySelector('.bottom-sheet-content') : null;
            if (sheetContent) sheetContent.appendChild(fab);
        }
    } else {
        // No items selected -> No FAB
        if (fab) {
            fab.remove();
        }
    }

    if (modal) {
        // Update content
        const bodyEl = modal.querySelector('.bottom-sheet-body'); // renderModal wraps body
        // Actually ui.mjs puts body in .sheet-scroll-body .modal-body
        const realBody = modal.querySelector('.modal-body');
        if (realBody) {
            realBody.innerHTML = '';
            realBody.appendChild(content);
        }

        // Update Title
        const headerH2 = modal.querySelector('.bottom-sheet-header h2'); // This selector might be wrong based on ui.mjs
        // ui.mjs: .sheet-header-section > .modal-title
        const modalTitle = modal.querySelector('.modal-title');
        if (modalTitle) {
            modalTitle.innerHTML = '';
            modalTitle.appendChild(titleContent);
        }
        const confirmMessage = t.deleteExpenseConfirm.replace('{name}', expense.description);
        const preDeleteCheck = async () => {
            const groupId = parseUrlForGroupId();
            if (!groupId) {
                return { allowed: false, message: 'Unable to determine group ID' };
            }

            return { allowed: true };
        };

        addDeleteButtonToModal(
            modal,
            async () => {
                try {
                    const groupId = parseUrlForGroupId();
                    // Call delete API
                    await deleteExpense(groupId, expense.id);

                    // Close modal
                    closeModal(modal);

                    // Navigate back to group home
                    router.navigate(`/home/${groupId}`);
                } catch (error) {
                    console.error('Failed to delete expense:', error);
                    alert('Failed to delete expense. Please try again.');
                }
            },
            confirmMessage,
            preDeleteCheck
        );

        // Receipt icon is now rendered inline in the footer body — no header icon needed
    } else {
        // Create New
        modal = renderModal({
            anchorEl: document.body,
            title: titleContent,
            body: content,
            buttons: [], // No default buttons
            onClose: () => {
                // De-register live-update callback
                modalRegistry.onGroupUpdate = null;
                // Replace the expense URL with the group URL (don't push to history)
                const groupId = parseUrlForGroupId();
                if (groupId) {
                    router.navigate(`/home/${groupId}`, true);
                }
            }
        });
        modal.id = 'receipt-view-modal';

        // Register live-update callback so subscription updates refresh the modal in-place
        modalRegistry.onGroupUpdate = (updatedGroup) => {
            const updatedExpense = updatedGroup.expenses.find(e => e.id === expense.id);
            if (updatedExpense) {
                // Re-render with selections preserved (resetSelections = false)
                renderReceiptView(updatedExpense, updatedGroup.participants, false);
            }
        };
        const confirmMessage = t.deleteExpenseConfirm.replace('{name}', expense.description);
        const preDeleteCheck = async () => {
            const groupId = parseUrlForGroupId();
            if (!groupId) {
                return { allowed: false, message: 'Unable to determine group ID' };
            }

            return { allowed: true };
        };

        addDeleteButtonToModal(
            modal,
            async () => {
                try {
                    const groupId = parseUrlForGroupId();
                    // Call delete API
                    await deleteExpense(groupId, expense.id);

                    // Close modal
                    closeModal(modal);

                    // Navigate back to group home
                    router.navigate(`/home/${groupId}`);
                } catch (error) {
                    console.error('Failed to delete expense:', error);
                    alert('Failed to delete expense. Please try again.');
                }
            },
            confirmMessage,
            preDeleteCheck
        );

        // Receipt icon is now rendered inline in the footer body — no header icon needed

        // Add FAB if needed (logic above handles updates, but here we need initial append if count > 0)
        // Oops, logic above assumes modal exists for finding FAB.
        // Let's restructure to be cleaner:
        // 1. Create Modal
        // 2. Add/Update FAB

        // Re-run FAB logic for new modal
        const sheetContent = modal.querySelector('.bottom-sheet-content');

        if (totalSelected > 0) {
            fab = createFAB({
                text: t.assignSelected,
                icon: USER_PLUS_ICON,
                onClick: (e) => {
                    e.stopPropagation();
                    openAssignmentModal(expense, participants);
                }
            });
            sheetContent.appendChild(fab);
        }

        modal.addEventListener('click', (e) => {
            if (e.target === modal) {
                const groupId = parseUrlForGroupId();
                if (groupId) router.navigate(`/home/${groupId}`, true);
                else closeModal(modal);
            }
        });
    }

    logMessage(`Rendered receipt modal for: ${expense.description}`, 'success');
}



// Helper to render receipt icon
export function renderHeaderReceiptIcon(container, expense, participants) {
    const userEmail = localStorage.getItem('splitpal_user_email');
    const isOwner = userEmail && userEmail === expense.itemizedData?.owner;

    if (expense.itemizedData && (expense.itemizedData.receiptAvailable || isOwner)) {
        const btn = document.createElement('button');
        btn.className = 'header-icon-btn receipt-icon-btn';
        btn.innerHTML = `
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                <polyline points="14 2 14 8 20 8"></polyline>
                <line x1="16" y1="13" x2="8" y2="13"></line>
                <line x1="16" y1="17" x2="8" y2="17"></line>
                <polyline points="10 9 9 9 8 9"></polyline>
            </svg>
        `;
        btn.style.cssText = 'background:none; border:none; cursor:pointer; padding:8px; color:#555; display:flex; align-items:center; justify-content:center;';
        btn.title = t.showReceipt;

        btn.onclick = (e) => {
            e.stopPropagation();
            openReceiptDrawer(expense, participants);
        };
        container.appendChild(btn);
    }
}
